import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { Request } from 'express';
import { AppLocation } from '../../../core/models/interface/geo';
import { Moment } from 'moment';
import * as moment from 'moment';
import { WaterBodyDAO } from '../../../core/services/dao/waterBodyDAO';
import { WaterBody } from 'anchor-shared/lib/model/entity/waterBody';
import { ActivatedRoute, Router } from '@angular/router';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ContainsSubscriptions } from '../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { Subscription } from 'rxjs/Rx';
import { fadeInUpBigOnEnter } from '../../../core/pure-functions/animations/ng-animations-provider';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { UserAgentDetection } from '../../../core/pure-functions/universal/user-agent-detection';
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepicker,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import { dateSessionStorageFormat } from '../../buyflow-home/buyflow-home.component';
import { CharterType } from 'anchor-shared/lib/model/enum/charter-type';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';

const DATE_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'MMM DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMM YYYY',
  },
};

@Component({
  selector: 'app-buyflow-search-bar',
  templateUrl: './buyflow-search-bar.component.html',
  styleUrls: ['./buyflow-search-bar.component.scss'],
  animations: [fadeInUpBigOnEnter()],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DefaultMatCalendarRangeStrategy,
    },
  ],
})
export class BuyflowSearchBarComponent
  implements OnInit, ContainsSubscriptions, OnDestroy
{
  charterType = CharterType.DayCharter;
  // Day
  availableOn: Moment;
  // Term
  availableFrom: Moment = null;
  availableTo: Moment = null;
  selectedDates;
  destinationInput: string = '';
  location: AppLocation;
  isMobile: boolean;
  subs: Subscription[] = [];
  startMobileFlow = false;
  startMobileDate = false;
  disableNext = true;

  @Output()
  nearestWaterBodyFound = new EventEmitter<WaterBody>();
  vanityUrl: any;
  isBrowser: boolean;
  isSmallDesktop: boolean;
  CharterType = CharterType;

  @ViewChild('datepickerDate', { static: false })
  datePicker: MatDatepicker<Moment>;

  @ViewChild('rangePicker', { static: false })
  rangePicker: MatDateRangePicker<Moment>;

  constructor(
    private waterBodyDAO: WaterBodyDAO,
    private router: Router,
    @Optional() @Inject(REQUEST) private request: Request,
    @Inject(PLATFORM_ID) private platformId: any,
    private breakpointObserver: BreakpointObserver,
    private route: ActivatedRoute,
  ) {
    if (isPlatformServer(this.platformId)) {
      if (UserAgentDetection.isMobileUserAgent(request.headers['user-agent'])) {
        this.isMobile = true;
      }
    }
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.getSessionStorageDates();

      const sub = this.breakpointObserver
        .observe(['(min-width: 960px)'])
        .subscribe((state: BreakpointState) => {
          this.isMobile = !state.matches;
        });
      this.subs.push(sub);
      const isSmallDesktopSub = this.breakpointObserver
        .observe(['(min-width: 1279px)'])
        .subscribe((state: BreakpointState) => {
          this.isSmallDesktop = !state.matches;
        });
      this.subs.push(isSmallDesktopSub);
    }
    this.initCharterType();
  }

  ngOnInit() {}

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  getSessionStorageDates() {
    [
      { type: 'filterDate', val: 'availableOn' },
      { type: 'filterDateRangeStart', val: 'availableFrom' },
      { type: 'filterDateRangeEnd', val: 'availableTo' },
    ].forEach(({ type, val }) => {
      const storedDate = moment(
        sessionStorage.getItem(type),
        dateSessionStorageFormat,
      );

      if (storedDate.isValid()) {
        this[val] = storedDate;
      }
    });
  }

  autocompleteSearchAddress(location: AppLocation) {
    this.location = location;
    this.findClosestWaterBodyAndSetVanityUrl();
  }

  onMobileAutoCompleteResult(location: AppLocation): void {
    if (location.addressComponents && location.addressComponents.length > 0) {
      this.location = location;
      this.startMobileFlow = false;
      this.startMobileDate = true;
      this.findClosestWaterBodyAndSetVanityUrl();
    }
  }

  findClosestWaterBodyAndSetVanityUrl() {
    const findWaterBodySub = this.waterBodyDAO
      .findClosestWaterBodyToPlacesInput(this.location)
      .subscribe((selectedWaterBody) => {
        this.vanityUrl = selectedWaterBody.vanityUrl;
      });
    this.subs.push(findWaterBodySub);
  }

  searchForWaterbody() {
    if (this.location) {
      if (!this.vanityUrl) {
        this.vanityUrl = 'tampa-bay';
      }
      const slug =
        this.location && this.location.vicinity
          ? this.location.vicinity
          : this.location.formattedAddress;

      let basePath = 'rental-location-map/';
      const queryParams: any = {
        charterType: this.charterType,
        latitude: this.location.position.lat,
        longitude: this.location.position.lng,
        zoom: 11,
        fromSearchBar: true,
      };

      if (this.charterType === CharterType.DayCharter) {
        queryParams.date = this.availableOn?.format('YYYY-MM-DD');
      } else {
        queryParams.dateStart = this.availableFrom?.format('YYYY-MM-DD');
        queryParams.dateEnd = this.availableTo?.format('YYYY-MM-DD');
      }

      this.router.navigate([basePath + slug], {
        queryParams,
      });
    }
  }

  dateFilter = (incomingMoment: Moment): boolean => {
    if (incomingMoment) {
      return !incomingMoment.isBefore(moment(), 'day');
    }
  };

  updateDateFilter() {
    if (this.isBrowser) {
      if (this.charterType === CharterType.DayCharter) {
        sessionStorage.setItem(
          'filterDate',
          this.availableOn.format(dateSessionStorageFormat),
        );
      } else {
        if (this.availableFrom?.isValid()) {
          sessionStorage.setItem(
            'filterDateRangeStart',
            this.availableFrom.format(dateSessionStorageFormat),
          );
        }
        if (this.availableTo?.isValid()) {
          sessionStorage.setItem(
            'filterDateRangeEnd',
            this.availableTo.format(dateSessionStorageFormat),
          );
        }
      }
    }
  }

  updateCalendarSelection(someDate: any): void {
    this.disableNext = false;
    this.availableOn = someDate;
    if (this.isBrowser) {
      sessionStorage.setItem(
        'filterDate',
        this.availableOn.format(dateSessionStorageFormat),
      );
    }
  }

  handleDestinationInput(input) {
    this.destinationInput = input;
  }

  setCharterType({ tab }) {
    if (tab.textLabel === 'Multi-day') {
      this.charterType = CharterType.TermCharter;
    } else {
      this.charterType = CharterType.DayCharter;
    }
  }

  nextClick(): void {
    this.searchForWaterbody();
  }

  skipClick(): void {
    this.searchForWaterbody();
  }

  openDatePickerIfNoDate() {
    if (
      (!this.availableOn ||
        (this.availableOn && !this.availableOn.isValid())) &&
      this.charterType === CharterType.DayCharter
    ) {
      this.datePicker.open();
    }
    if (
      (!this.availableFrom || !this.availableTo) &&
      this.charterType === CharterType.TermCharter
    ) {
      this.rangePicker.open();
    }
  }

  setDayAndMonth(
    normalizedDayAndMonth: Moment,
    datepicker: MatDatepicker<Moment>,
  ) {
    const ctrlValue = this.availableOn ?? moment();
    ctrlValue.day(normalizedDayAndMonth.day());
    ctrlValue.month(normalizedDayAndMonth.month());
    this.availableOn = ctrlValue;
    datepicker.close();
  }

  handleDatePickerOpen() {
    if (this.charterType === 'DayCharter') {
      this.datePicker.open();
    } else {
      this.rangePicker.open();
    }
  }

  handleStartRangeChange(startDate) {
    this.availableFrom = moment(startDate, dateSessionStorageFormat);
  }

  handleEndRangeChange(endDate) {
    this.availableTo = moment(endDate, dateSessionStorageFormat);
  }

  onSelectedChange(date: Date): void {
    if (
      moment(date).isAfter(this.availableFrom) &&
      !this.availableTo &&
      this.availableFrom
    ) {
      this.availableTo = moment(date, dateSessionStorageFormat);
      this.selectedDates = new DateRange(this.availableFrom, this.availableTo);
      this.disableNext = false;
    } else {
      this.availableFrom = moment(date, dateSessionStorageFormat);
      this.selectedDates = new DateRange(this.availableFrom, null);
      this.disableNext = true;
      this.availableTo = null;
    }
  }

  initCharterType() {
    const queryType = this.route.snapshot.queryParams.charterType;
    if (queryType?.toLowerCase() === CharterType.TermCharter.toLowerCase()) {
      this.charterType = CharterType.TermCharter;

      if (this.route.snapshot.queryParams.charterType !== this.charterType) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            charterType: this.charterType,
          },
          queryParamsHandling: 'merge',
        });
      }
    }
  }
}
